import { BetrokkenTrein, usePutBusEvacuatieEtas } from "@/entities/betrokken-treinen";
import { RoleGate, UserRole } from "@/features/auth";
import { BusEvacuatieEtaBewerkenDialog } from "@/features/betrokken-treinen/components/evacuatie-bus/bus-evacuatie-eta-bewerken-dialog.tsx";
import { Button, Icon, Separated } from "@/shared/components";
import { formatDateTimeRelative } from "@/shared/utils";
import { useState } from "react";
import { BusEvacuatieEtaToevoegenDialog } from "./bus-evacuatie-eta-toevoegen-dialog";

type EvacuatieBusEtasProps = {
    dossierId: string;
    trein: BetrokkenTrein;
    etas: Date[];
};
export const EvacuatieBusEtas = ({ dossierId, trein, etas }: EvacuatieBusEtasProps) => {
    const putEtas = usePutBusEvacuatieEtas();
    const [currentlyEditingIndex, setCurrentlyEditingIndex] = useState<number | null>(null);

    const editEta = (index: number, newEta: Date) => {
        const newEtas = etas.map((eta, i) => (i === index ? newEta : eta));
        putEtas.mutate(
            { dossierId, treinId: trein.id, etas: newEtas.map(eta => eta.toISOString()) },
            {
                onSuccess: () => setCurrentlyEditingIndex(null),
            }
        );
    };

    const canEdit =
        trein.busEvacuatie?.status === "Aanvraag" ||
        trein.busEvacuatie?.status === "Gedeeltelijk" ||
        trein.busEvacuatie?.status === "Volledig";

    return (
        <div className="grid grid-cols-1 content-start">
            <label className="leading-l text-s text-content-emphasis font-bold">ETA evacuatiebussen</label>
            <RoleGate roles={["regievoerder"]}>
                <div className="flex justify-center">
                    {!etas.length && <p className="text-s text-content-subtle mt-4">Er zijn nog geen ETAs bekend</p>}
                </div>
            </RoleGate>
            <Separated>
                {etas.map((eta, busNummer) => (
                    <div key={`bus-${busNummer + 1}`} className="flex items-center gap-2 py-2 text-sm">
                        <Icon name="bus" className="text-content-emphasis h-6 w-6" />
                        <span className="text-content-emphasis pl-2 font-medium">{busNummer + 1}</span>
                        {eta && <span className="text-content-subtle text-s ml-auto">ETA {formatDateTimeRelative(eta)}</span>}
                        <RoleGate roles={[UserRole.TRANSVISION, UserRole.DEWIT]}>
                            {canEdit && (
                                <>
                                    <Button
                                        className="text-content-subtle"
                                        variant="tertiary"
                                        iconOnly
                                        icon="edit"
                                        onClick={() => setCurrentlyEditingIndex(busNummer)}
                                    />
                                    {currentlyEditingIndex === busNummer && (
                                        <BusEvacuatieEtaBewerkenDialog
                                            open={currentlyEditingIndex === busNummer}
                                            setOpen={(open) => setCurrentlyEditingIndex(open ? busNummer : null)}
                                            trein={trein}
                                            currentEta={eta}
                                            onSubmit={(newEta) => editEta(busNummer, newEta)}
                                            isPending={putEtas.isPending}
                                            error={putEtas.error}
                                        />
                                    )}
                                </>
                            )}
                        </RoleGate>
                    </div>
                ))}
            </Separated>
            <RoleGate roles={[UserRole.TRANSVISION, UserRole.DEWIT]}>
                {canEdit && (
                    <div className="flex shrink grow-0 items-center justify-start">
                        <BusEvacuatieEtaToevoegenDialog dossierId={dossierId} trein={trein}>
                            <Button variant="secondary" icon="plus" size="s" className="mt-2">
                                ETA evacuatiebus toevoegen
                            </Button>
                        </BusEvacuatieEtaToevoegenDialog>
                    </div>
                )}
            </RoleGate>
        </div>
    );
};
